import type {
  EnhancedStoreCustomer,
  StoreCustomerMetadata,
  StoreCustomerMetadata$SalesChannelSetting,
} from '@marxbiotech/shared-types/medusa';

// =============================================================================
// Single-channel setting variants (for BFF layout data)
// Design Decision: 參數型別使用 `| null` 而非 `| null | undefined`，
// 呼叫端需明確以 `?? null` 轉換。這讓每個 call site 的 nullish 處理意圖更清晰，
// 且與函數內部 `!setting?.is_allowed_visit` 的 null check 語意一致。
// =============================================================================

/**
 * 從單一 sales channel setting 判斷結算類型。
 * 用於 BFF layout 回傳的 salesChannelSetting（已對應到當前 tenant）。
 *
 * Priority: monthly > per-order > normal > null
 * - `monthly`   — deposit-with-loan enabled AND settlement_type is 'monthly'
 * - `per-order`  — deposit-with-loan enabled AND settlement_type is 'per-order'
 * - `normal`     — deposit enabled, OR deposit-with-loan enabled with other settlement_type
 * - `null`       — not allowed to visit, or no qualifying payment provider
 */
export const getSettlementTypeBySetting = (
  setting: StoreCustomerMetadata$SalesChannelSetting | null
): 'monthly' | 'per-order' | 'normal' | null => {
  if (!setting?.is_allowed_visit) return null;

  const depositWithLoanSetting =
    setting.payment_provider_settings['pp_marx_deposit-with-loan_marx'];
  const depositSetting =
    setting.payment_provider_settings.pp_marx_deposit_marx;

  if (
    depositWithLoanSetting?.is_enabled &&
    depositWithLoanSetting.settlement_type === 'monthly'
  ) {
    return 'monthly';
  }

  if (
    depositWithLoanSetting?.is_enabled &&
    depositWithLoanSetting.settlement_type === 'per-order'
  ) {
    return 'per-order';
  }

  if (depositSetting?.is_enabled || depositWithLoanSetting?.is_enabled) {
    return 'normal';
  }

  return null;
};

/**
 * 從單一 sales channel setting 判斷是否啟用儲值金付款
 * 用於 BFF layout 回傳的 salesChannelSetting（已對應到當前 tenant）
 */
export const getPaymentDepositEnabledBySetting = (
  setting: StoreCustomerMetadata$SalesChannelSetting | null
): boolean => {
  if (!setting?.is_allowed_visit) return false;

  return (
    (setting.payment_provider_settings.pp_marx_deposit_marx?.is_enabled ?? false) ||
    (setting.payment_provider_settings['pp_marx_deposit-with-loan_marx']?.is_enabled ?? false)
  );
};

// =============================================================================
// Customer-based variants (using customer.metadata.sales_channel_settings)
// Used when we have the full customer object and need to check all channels
// =============================================================================

/**
 * 取得當前活躍的 sales channel setting
 * 從所有 sales channel settings 中找出 is_allowed_visit 為 true 的設定
 */
const getActiveSalesChannelSetting = (
  customer: EnhancedStoreCustomer | null
): StoreCustomerMetadata$SalesChannelSetting | null => {
  if (!customer) return null;

  const metadata = customer.metadata as StoreCustomerMetadata | undefined;
  const settings = metadata?.sales_channel_settings;
  if (!settings) return null;

  // 找出第一個有權限的 sales channel
  const activeSetting = Object.values(settings).find(
    (setting) => setting.is_allowed_visit
  );
  return activeSetting ?? null;
};

/**
 * 從 customer 判斷結算類型
 */
export const getSettlementTypeByCustomer = (
  customer: EnhancedStoreCustomer | null
): 'monthly' | 'per-order' | 'normal' | null => {
  const setting = getActiveSalesChannelSetting(customer);
  return getSettlementTypeBySetting(setting);
};

/**
 * 判斷客戶是否為月結客戶
 */
export const isMonthlySettlementCustomer = (
  customer: EnhancedStoreCustomer | null
): boolean => {
  return getSettlementTypeByCustomer(customer) === 'monthly';
};

/**
 * 判斷客戶是否為每單結算客戶
 */
export const isPerOrderSettlementCustomer = (
  customer: EnhancedStoreCustomer | null
): boolean => {
  return getSettlementTypeByCustomer(customer) === 'per-order';
};

/**
 * 判斷客戶是否為一般結算客戶
 */
export const isNormalSettlementCustomer = (
  customer: EnhancedStoreCustomer | null
): boolean => {
  return getSettlementTypeByCustomer(customer) === 'normal';
};
